// https://w3c.github.io/aria-practices/examples/menubar/menubar-navigation.html
import ClassNames from "./classNames.js";
import DomElements from "./domElements.js";
const {
  $navContainerWrapper,
  $navPrimaryMainItems: $mainItems,
  $navPrimarySubNavs: $subNavs,
  $navPrimarySubNavGroups: $subNavGroups,
} = DomElements;
import Shared from "./shared.js";
const {
  getActiveSubmenuId,
  updateActiveSubmenuId,
  closeAllSubmenus,
  toggle,
  isAnySubmenuOpen,
} = Shared;
import Overlay from "./handleOverlay.js";
const { reevaluateOverlay } = Overlay;

/**
 * Calculates width of each $subnav based on navigation container width and
 * number of child nav groups
 */
const calcSubNavsWidth = () => {
  const subNavList = Array.prototype.slice.call($subNavs, 0);
  subNavList.forEach(($subNav) => {
    const navGroupsCount = parseInt($subNav.dataset.columns);

    // Gutter sizes for >= 1440
    let gutterInner = 32;
    let gutterOuter = 32;

    if (window.innerWidth < 1440) {
      gutterInner = 24;
    }

    if (window.innerWidth < 1024) {
      gutterInner = 16;
      gutterOuter = 24;
    }

    // Calculate menu width without padding
    const computedStyle = getComputedStyle($navContainerWrapper);
    const menuWidth =
      $navContainerWrapper.clientWidth -
      parseFloat(computedStyle.paddingLeft) -
      parseFloat(computedStyle.paddingRight);

    // Calculate width of each subnav for screens equal or larger the 1024 (M up)
    if (window.innerWidth >= 1024) {
      const columnWidth = (menuWidth - gutterInner * 3) / 4;
      const width =
        columnWidth * navGroupsCount +
        (navGroupsCount - 1) * gutterInner +
        gutterOuter * 2;
      $subNav.style.width = `${width}px`;
    }

    // Set width of each subnav for breakpoint 768 - 1024 (M)
    // (As determined by design)
    if (window.innerWidth >= 768 && window.innerWidth < 1024) {
      $subNav.style.width = `${window.innerWidth}px`; // 100%
      if (navGroupsCount === 2) $subNav.style.width = "572px"; // Fixed number
      if (navGroupsCount === 1) $subNav.style.width = "302px"; // Fixed number
    }

    // Reset calculated width of each subnav for screens smaller then 768 (S down)
    if (window.innerWidth < 768) $subNav.style.width = "";
  });
};

/**
 * Handle initial setup
 */
function initSubmenus() {
  handleActiveState();
  initKeyboardSupport();

  const mainItemList = Array.prototype.slice.call($mainItems, 0);
  mainItemList.forEach(($menuItem) => {
    $menuItem.addEventListener("click", (event) => {
      const { submenuId: requestedSubmenuId } = $menuItem.dataset;

      // If no related submenu
      if (!requestedSubmenuId) return;

      // Prevent default behaviour links
      event.preventDefault();

      // Close open submenu
      if (getActiveSubmenuId()) toggle(getActiveSubmenuId(), "close");

      // If requested- same as active submenu
      if (getActiveSubmenuId() && getActiveSubmenuId() === requestedSubmenuId) {
        updateActiveSubmenuId("");
      } else {
        updateActiveSubmenuId(requestedSubmenuId);
        toggle(requestedSubmenuId, "open");
      }
      reevaluateOverlay();
    });
  });

  // Set data attribute for each submenu with number of columns (for CSS)
  $subNavs.forEach(($submenu) => {
    const columns = $submenu.querySelectorAll(
      `.${ClassNames.navPrimary.subNavGroup}`
    ).length;
    $submenu.dataset.columns = columns.toString();
  });

  calcSubNavsWidth();
}

/**
 * Keyboard support (A11Y)
 */
const initKeyboardSupport = () => {
  // For main items
  const mainItemList = Array.prototype.slice.call($mainItems, 0);
  mainItemList.forEach(($item, i) => {
    $item.addEventListener("keyup", (event) => {
      switch (event.code) {
        case "ArrowDown":
        case "ArrowRight":
          i + 1 in $mainItems && $mainItems[i + 1].focus();
          break;
        case "ArrowUp":
        case "ArrowLeft":
          i - 1 in $mainItems && $mainItems[i - 1].focus();
          break;
        case "Home":
          $mainItems[0].focus();
          break;
        case "End":
          $mainItems[$mainItems.length - 1].focus();
          break;
      }
    });
  });

  // For sub nav items
  const subNavGroupList = Array.prototype.slice.call($subNavGroups, 0);
  subNavGroupList.forEach(($subNavGroup) => {
    const $links = $subNavGroup.querySelectorAll("a");
    const linkList = Array.prototype.slice.call($links, 0);
    linkList.forEach(($link, i) => {
      $link.addEventListener("keyup", (event) => {
        switch (event.code) {
          case "ArrowDown":
          case "ArrowRight":
            i + 1 in $links && $links[i + 1].focus();
            break;
          case "ArrowUp":
          case "ArrowLeft":
            i - 1 in $links && $links[i - 1].focus();
            break;
          case "Home":
            $links[0].focus();
            break;
          case "End":
            $links[$links.length - 1].focus();
            break;
          case "Escape":
            closeAllSubmenus();
            reevaluateOverlay();
            $link.parentNode?.parentNode?.parentNode?.parentNode?.parentNode
              ?.querySelector("button")
              ?.focus();
            break;
        }
      });
    });
  });
};

/**
 * Apply a visual active state based on pathname matching (window href and link href)
 *
 * (The API always provides an absolute path)
 */
const handleActiveState = () => {
  function addActiveClass($node) {
    $node.classList.add("shared-header__nav__link--is-active");
  }

  const mainItemList = Array.prototype.slice.call($mainItems, 0);
  mainItemList.forEach(($menuItem) => {
    let menuItemLink = document.createElement("a");

    if ($menuItem.nodeName === "A") menuItemLink.href = $menuItem.href;
    if ($menuItem.nodeName === "BUTTON")
      menuItemLink.href = $menuItem.dataset.href;

    if (menuItemLink) {
      const currentLocation = window.location;
      const isHomeLink = $menuItem.dataset.isHomeItem;
      const isPathPartOfCurrentLocation =
        currentLocation.pathname.toLowerCase().indexOf(menuItemLink.pathname.toLowerCase()) !== -1;

      if (currentLocation.hostname.toLowerCase() !== menuItemLink.hostname.toLowerCase()) {
        // External site link, do nothing
        return;
      }

      if (isHomeLink) {
        if (menuItemLink.pathname.toLowerCase() === currentLocation.pathname.toLowerCase()) {
          addActiveClass($menuItem);
        }
      } else {
        if (isPathPartOfCurrentLocation) {
          addActiveClass($menuItem);
          openSubMenuAndScroll($menuItem, currentLocation.pathname);
        }
      }
    }
  });
};

/**
 * Open menu on mobile devices and scroll to clicked link
 */
function openSubMenuAndScroll($menuItem, pathname) {
  if (window.innerWidth < 768) {
    const { submenuId: requestedSubmenuId } = $menuItem.dataset;
    if (!requestedSubmenuId) return;
    updateActiveSubmenuId(requestedSubmenuId);
    // open the submenu
    toggle(requestedSubmenuId, "open");

    // scroll to the clicked link
    const subNavList = Array.prototype.slice.call($subNavs, 0);
    subNavList.forEach(($submenu) => {
      if ($submenu.dataset.submenuId === requestedSubmenuId) {
        const $links = $submenu.querySelectorAll("a");
        const linkList = Array.prototype.slice.call($links, 0);
        linkList.forEach(($link) => {
          if (pathname.toLowerCase() === $link.pathname.toLowerCase()) {
            $link.scrollIntoView(false);
          }
        });
      }
    });
  }
}

export default {
  calcSubNavsWidth,
  closeAllSubmenus,
  initSubmenus,
  isAnySubmenuOpen,
};
